import LicensePlate from "license-plate";
import { GraphQLClient } from "../../../../shared/gql-client";
import { CarModel, CarResponseModel } from "../../domain/models/carModel";
import * as gqlBuilder from "gql-query-builder";
import { gql } from "@apollo/client";
import { tirePositions } from "../../domain/lists/tirePositionList";
import { CarTirePosition } from "../../domain/models/carTireModel";

export default class CarService {
  private client: GraphQLClient | undefined;

  setClient(client: GraphQLClient) {
    this.client = client;
  }

  delete(id: number): Promise<CarModel> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "deleteCar",
        variables: {
          id: {
            name: "id",
            type: "ID!",
            value: id,
          },
        },
        fields: ["id", "licence", "status", "vin", "additional_info", "label"],
      });

      this.client
        ?.mutate("deleteCar", gql(query), variables)
        .then((response) => {
          const data = response as CarResponseModel;
          const licencePlate = new LicensePlate(data.licence);

          resolve({
            id: data.id,
            licencePlate: licencePlate.valid()
              ? licencePlate.pretty()
              : data.licence,
            additional_info: data.additional_info,
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  mutate(car: CarModel): Promise<CarModel> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.mutation({
        operation: "upsertCompleteCar",
        variables: {
          input: {
            value: {
              ...(car.id !== undefined ? { id: car.id } : {}),
              licence: car?.licencePlate?.replace(/-/g, ""),
              vin: car.vin,
              additional_info: car.additional_info,
              freestock_tire:
                car.freestock_tire == undefined
                  ? undefined
                  : !!car.freestock_tire,
              status: !!car.status,
              is_lease: !!car.is_lease,
              relation_id: car.relationId,
              comment: car.comment,
              customer_id: car.customerId ? car.customerId : null,
              tiresets: {
                upsert: car.tireSets?.map((tireSet) => {
                  return {
                    id: tireSet.id !== undefined ? tireSet.id : null,
                    type: tireSet.type,
                    sleep_set: tireSet.sleepingSet,
                    position_id: tireSet.position_id,
                    tire_rim: tireSet.tire_rim,
                    comment: tireSet.comment,
                    tires: {
                      upsert: tireSet.tires?.map((tire) => {
                        return {
                          id: tire.id !== undefined ? tire.id : null,
                          brand: tire.brand,
                          brand_type: tire.brandType,
                          profile_depth: tire.profile,
                          ean:
                            tire.ean && tire.ean.length > 0 ? tire.ean : null,
                          size: tire.size,
                          run_flat: tire.run_flat,
                          car_position: tire.car_position,
                        };
                      }),
                    },
                  };
                }),
              },
            },
            type: "upsertCarInput",
            required: true,
          },
        },
        fields: [
          "id",
          "licence",
          "vin",
          "additional_info",
          "freestock_tire",
          "label",
          "status",
          {
            tiresets: [
              "id",
              "type",
              "sleep_set",
              "in_hotel",
              "tire_rim",
              {
                tires: [
                  "id",
                  "brand",
                  "brand_type",
                  "type",
                  "profile_depth",
                  "size",
                  "run_flat",
                  "car_position",
                  "status",
                ],
              },
            ],
          },
          {
            relation: ["id"],
          },
        ],
      });

      this.client
        ?.mutate("upsertCompleteCar", gql(query), variables)
        .then((response) => {
          const data = response as CarResponseModel;
          const licencePlate = new LicensePlate(data?.licence || "");
          resolve({
            id: data.id,
            licencePlate: licencePlate.valid()
              ? licencePlate.pretty()
              : data.licence,
            vin: data.vin,
            additional_info: data.additional_info,
            freestock_tire: data.freestock_tire,
            label: data.label,
            status: data.status == 1,
            is_lease: data.is_lease == 1,
            comment: data.comment,
            customerId: data?.customer?.id ?? undefined,
            tireSets: data.tiresets?.map((t) => ({
              id: t.id,
              type: t.type,
              sleepingSet: t.sleep_set,
              inHotel: t.in_hotel,
              tire_rim: t.tire_rim,
              comment: t.comment,
              tires: t.tires?.map((tire, i) => ({
                id: tire.id,
                brand: tire.brand,
                brandType: tire.brand_type,
                type: tire.type,
                profile: tire.profile_depth,
                size: tire.size,
                run_flat: tire.run_flat,
                car_position: tirePositions[i] as CarTirePosition,
                status: t.status,
              })),
            })),
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
